import type { M3pProjection, PlanYears, PortalDashboard } from "@dearbill/shared";

/** The plan-year slice of the outlet context: the years on offer, the selected one and its projection. */
export interface PlanYearSelection {
  /**
   * The years the dashboard payload prices, in the shape the shared toggle
   * reads: `current` from `m3p`, `next` from `m3pNextYear` (null without one,
   * so no toggle renders), and that block's out-of-pocket threshold.
   */
  years: PlanYears;
  /** The selected plan year, `years.current` or `years.next`. */
  planYear: number;
  /** The projection for `planYear`: `m3pNextYear` under next year, else `m3p`. */
  m3p: M3pProjection | null;
}

/**
 * Resolves the plan year the dashboard screens show from the payload and the
 * member's pick (null until the toggle is used). Without a next-year block the
 * answer is the current year whatever the pick or the saved flag says. With
 * one, the pick wins, else the enrollment's saved `planYear` opens next year
 * when it is above the current year.
 */
export function selectPlanYear(data: PortalDashboard, picked: number | null): PlanYearSelection {
  const years: PlanYears = {
    current: data.m3p?.contractYear ?? new Date().getFullYear(),
    next: data.m3pNextYear?.contractYear ?? null,
    nextOopThreshold: data.m3pNextYear?.oopThreshold ?? null,
  };
  if (years.next === null) return { years, planYear: years.current, m3p: data.m3p };
  const saved = data.latestEnrollment?.planYear ?? years.current;
  const planYear = picked ?? (saved > years.current ? years.next : years.current);
  return {
    years,
    planYear,
    m3p: planYear === years.next ? data.m3pNextYear : data.m3p,
  };
}
